/**
 * ProfileSection
 * Layout 3-cols: sidebar (score + recoms) · radar (5 scores) · cards (Ingresos / Gastos / Recargas).
 * En pantallas <lg colapsa a 1-col.
 */
import { Lightbulb } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { useFinancialProfile } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import { CreditLevel } from '@/types/credit.types';
import type { FinancialProfileEntity } from '@/types/backoffice-crud.types';

interface ProfileSectionProps {
  directoryId: string;
}

export function ProfileSection({ directoryId }: ProfileSectionProps) {
  const { profile, isLoading, isError } = useFinancialProfile(directoryId);

  if (isLoading) return <div className="p-4 text-sm text-muted-foreground">Cargando perfil financiero…</div>;
  if (isError || !profile) {
    return <div className="p-4 text-sm text-muted-foreground">Sin perfil financiero calculado para este cliente.</div>;
  }

  return (
    <div className="p-3">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1.4fr_1.1fr] lg:items-stretch">
        <Sidebar profile={profile} />
        <RadarBlock profile={profile} />
        <RightCards profile={profile} />
      </div>
    </div>
  );
}

// ── Sidebar ─────────────────────────────────────────────────────────────────

function Sidebar({ profile }: { profile: FinancialProfileEntity }) {
  const levelIsValid = profile.credit_level
    ? Object.values(CreditLevel).includes(profile.credit_level as CreditLevel)
    : false;
  const score = profile.score_total ?? 0;
  const ringColor = scoreColor(score);
  const progressPct = Math.min(100, Math.max(0, profile.progress_pct ?? 0));

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-card p-4">
      {/* Gauge + nivel + progreso */}
      <div className="flex flex-col items-center text-center">
        <ScoreGauge value={score} color={ringColor} />
        {levelIsValid && profile.credit_level && (
          <div className="mt-2">
            <CreditLevelBadge level={profile.credit_level as CreditLevel} size="sm" />
          </div>
        )}
        {profile.next_level && (
          <p className="mt-2 text-xs text-muted-foreground">
            → <span className="font-semibold text-primary">{profile.next_level}</span>
          </p>
        )}
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">
          {progressPct.toFixed(0)}% · faltan {profile.score_to_next_level ?? 0} pts
        </p>
      </div>

      {/* Mini KPIs */}
      <div className="grid grid-cols-2 gap-2">
        <MiniKpi
          label="Crédito máx"
          value={formatCurrency(profile.max_credit_amount_mxn, 'MXN', 'es-MX')}
          accent="green"
        />
        <MiniKpi
          label="Sig. nivel"
          value={profile.next_level_max_credit_mxn != null
            ? formatCurrency(profile.next_level_max_credit_mxn, 'MXN', 'es-MX')
            : '—'}
          accent="green"
        />
        <MiniKpi label="Antigüedad" value={`${profile.months_in_platform} meses`} />
        <MiniKpi
          label="Elegible"
          value={
            <Badge variant={profile.is_eligible === 'YES' ? 'success' : 'danger'}>
              {profile.is_eligible === 'YES' ? 'Sí' : 'No'}
            </Badge>
          }
        />
      </div>

      {profile.ineligibility_reason && (
        <div className="rounded-md border-l-[3px] border-red-500 bg-red-500/10 px-3 py-1.5 text-xs text-red-700 dark:text-red-400">
          {profile.ineligibility_reason}
        </div>
      )}

      {/* Recomendaciones */}
      {(profile.recommendation_1 || profile.recommendation_2) && (
        <div className="mt-auto rounded-r-md border-l-[3px] border-blue-500 bg-blue-500/10 px-3 py-2">
          <p className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
            <Lightbulb size={11} /> Recomendaciones
          </p>
          {profile.recommendation_1 && (
            <RecommendationItem
              text={profile.recommendation_1}
              points={profile.recommendation_1_points}
              isFirst
            />
          )}
          {profile.recommendation_2 && (
            <RecommendationItem
              text={profile.recommendation_2}
              points={profile.recommendation_2_points}
            />
          )}
        </div>
      )}
    </div>
  );
}

function MiniKpi({
  label,
  value,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  accent?: 'green';
}) {
  const valueClass = accent === 'green' ? 'text-green-600 dark:text-green-400' : 'text-foreground';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={`text-sm font-bold tabular-nums ${valueClass}`}>{value}</span>
    </div>
  );
}

function RecommendationItem({
  text,
  points,
  isFirst,
}: {
  text: string;
  points: number | null | undefined;
  isFirst?: boolean;
}) {
  return (
    <div className={`py-1 text-xs text-blue-700 dark:text-blue-300 ${isFirst ? '' : 'border-t border-blue-500/20'}`}>
      <p>{text}</p>
      {points != null && (
        <span className="mt-1 inline-block rounded bg-blue-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:text-blue-300">
          +{points} pts
        </span>
      )}
    </div>
  );
}

function ScoreGauge({ value, color }: { value: number; color: string }) {
  const clamped = Math.min(100, Math.max(0, value));
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;
  return (
    <div className="relative inline-flex h-[136px] w-[136px] items-center justify-center">
      <svg width="136" height="136" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="50" cy="50" r={radius} fill="none" stroke="var(--color-muted, #334155)" strokeWidth="6" />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 700ms ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold tabular-nums text-foreground">{value.toFixed(value % 1 === 0 ? 0 : 1)}</span>
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">de 100</span>
      </div>
    </div>
  );
}

// ── Radar ───────────────────────────────────────────────────────────────────

interface RadarAxis {
  label: string;
  weight: number;
  angleDeg: number;
  anchor: 'middle' | 'start' | 'end';
  scoreKey: keyof FinancialProfileEntity;
}

const RADAR_AXES: RadarAxis[] = [
  { label: 'Estabilidad ingresos', weight: 30, angleDeg: -90, anchor: 'middle', scoreKey: 'score_income_stability' },
  { label: 'Capacidad pago',       weight: 25, angleDeg: -18, anchor: 'start',  scoreKey: 'score_payment_capacity' },
  { label: 'Transf. México',       weight: 20, angleDeg: 54,  anchor: 'middle', scoreKey: 'score_mexico_transfers' },
  { label: 'Antigüedad',           weight: 15, angleDeg: 126, anchor: 'middle', scoreKey: 'score_seniority' },
  { label: 'Nivel ingresos',       weight: 10, angleDeg: -162, anchor: 'end',   scoreKey: 'score_income_level' },
];

const RADIUS_MAX = 50;
const VIEW_W = 220;
const VIEW_H = 150;
const VIEW_X = -110;
const VIEW_Y = -75;

function polarToCart(angleDeg: number, radius: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: radius * Math.cos(rad), y: radius * Math.sin(rad) };
}

function pentagonPoints(level: number): string {
  // level: 0..1
  return RADAR_AXES.map((axis) => {
    const { x, y } = polarToCart(axis.angleDeg, RADIUS_MAX * level);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');
}

function RadarBlock({ profile }: { profile: FinancialProfileEntity }) {
  const dataPoints = RADAR_AXES.map((axis) => {
    const raw = profile[axis.scoreKey];
    const score = typeof raw === 'number' ? raw : 0;
    const clamped = Math.min(100, Math.max(0, score));
    const { x, y } = polarToCart(axis.angleDeg, (clamped / 100) * RADIUS_MAX);
    return { axis, score, x, y };
  });
  const polyPoints = dataPoints.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');

  return (
    <div className="flex flex-col rounded-md border border-border bg-card p-3">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Desglose de scores
      </p>

      <div className="flex flex-1 items-center justify-center">
        <svg
          viewBox={`${VIEW_X} ${VIEW_Y} ${VIEW_W} ${VIEW_H}`}
          className="w-full"
          style={{ maxWidth: 380 }}
          aria-label="Radar de scores"
        >
          {/* Grid concéntrico */}
          {[1, 0.75, 0.5, 0.25].map((level) => (
            <polygon
              key={level}
              points={pentagonPoints(level)}
              fill="none"
              stroke="var(--color-border, #334155)"
              strokeOpacity={0.6}
              strokeWidth={0.7}
            />
          ))}
          {/* Ejes */}
          {RADAR_AXES.map((axis) => {
            const end = polarToCart(axis.angleDeg, RADIUS_MAX);
            return (
              <line
                key={axis.label}
                x1="0"
                y1="0"
                x2={end.x.toFixed(2)}
                y2={end.y.toFixed(2)}
                stroke="var(--color-border, #475569)"
                strokeOpacity={0.5}
                strokeWidth={0.5}
              />
            );
          })}
          {/* Polígono de datos */}
          <polygon
            points={polyPoints}
            fill="rgba(59,130,246,0.30)"
            stroke="#3b82f6"
            strokeWidth={1.5}
          />
          {/* Puntos en cada vértice */}
          {dataPoints.map((p) => (
            <circle key={p.axis.label} cx={p.x.toFixed(2)} cy={p.y.toFixed(2)} r={2} fill="#3b82f6" />
          ))}
          {/* Labels */}
          {RADAR_AXES.map((axis) => {
            const score = dataPoints.find((p) => p.axis.label === axis.label)?.score ?? 0;
            const labelPos = labelPosition(axis);
            const color = scoreColor(score);
            return (
              <g key={axis.label}>
                <text
                  x={labelPos.x.toFixed(2)}
                  y={labelPos.yLabel.toFixed(2)}
                  textAnchor={axis.anchor}
                  fill={color}
                  fontSize={6}
                  fontWeight={600}
                >
                  {axis.label}
                </text>
                <text
                  x={labelPos.x.toFixed(2)}
                  y={labelPos.yNum.toFixed(2)}
                  textAnchor={axis.anchor}
                  fill={color}
                  fontSize={7.5}
                  fontWeight={700}
                >
                  {score}
                </text>
                <text
                  x={labelPos.x.toFixed(2)}
                  y={labelPos.yPct.toFixed(2)}
                  textAnchor={axis.anchor}
                  fill="var(--color-muted-foreground, #64748b)"
                  fontSize={5.5}
                >
                  peso {axis.weight}%
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Leyenda de umbrales */}
      <div className="mt-2 flex justify-center gap-3 border-t border-border pt-2 text-[10px] text-muted-foreground">
        <LegendDot color="#22c55e" label="≥70" />
        <LegendDot color="#eab308" label="40-69" />
        <LegendDot color="#ef4444" label="<40" />
      </div>
    </div>
  );
}

function labelPosition(axis: RadarAxis): { x: number; yLabel: number; yNum: number; yPct: number } {
  // Ubicación radial del label (más afuera del pentágono)
  // Top y bottom: alineamos labels con el eje vertical
  // Right y left: alineamos en x con el extremo + offset
  const isTop = axis.angleDeg === -90;
  const isBottomRight = axis.angleDeg === 54;
  const isBottomLeft = axis.angleDeg === 126;
  const isTopRight = axis.angleDeg === -18;
  const isTopLeft = axis.angleDeg === -162;

  if (isTop) {
    return { x: 0, yLabel: -65, yNum: -58, yPct: -52 };
  }
  if (isTopRight) {
    return { x: 55, yLabel: -22, yNum: -13, yPct: -7 };
  }
  if (isBottomRight) {
    return { x: 35, yLabel: 50, yNum: 58, yPct: 64 };
  }
  if (isBottomLeft) {
    return { x: -35, yLabel: 50, yNum: 58, yPct: 64 };
  }
  if (isTopLeft) {
    return { x: -55, yLabel: -22, yNum: -13, yPct: -7 };
  }
  return { x: 0, yLabel: -60, yNum: -53, yPct: -47 };
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </span>
  );
}

function scoreColor(score: number): string {
  if (score >= 70) return '#22c55e';
  if (score >= 40) return '#eab308';
  return '#ef4444';
}

// ── Right cards ─────────────────────────────────────────────────────────────

function RightCards({ profile }: { profile: FinancialProfileEntity }) {
  return (
    <div className="grid gap-2.5 lg:grid-rows-3">
      <DataCard title="Ingresos">
        <DataRow label="Mensual USD" value={formatCurrency(profile.monthly_income_usd, 'USD')} />
        <DataRow label="Mensual MXN" value={formatCurrency(profile.monthly_income_mxn, 'MXN', 'es-MX')} />
        <DataRow label="Total 90d (USD)" value={formatCurrency(profile.total_income_90d_usd, 'USD')} />
        <DataRow
          label="Regular"
          value={
            <Badge variant={profile.has_regular_income === 'YES' ? 'success' : 'danger'}>
              {profile.has_regular_income === 'YES' ? `Sí · ${profile.dominant_frequency}` : 'No'}
            </Badge>
          }
        />
        <DataRow
          label="Fuentes"
          value={`${profile.total_income_sources} (${profile.primary_sources} prim · ${profile.secondary_sources} sec)`}
        />
      </DataCard>

      <DataCard title="Gastos y saldos">
        <DataRow label="Gasto mensual" value={formatCurrency(profile.monthly_expenses_usd, 'USD')} />
        <DataRow
          label="Ratio gastos"
          value={<RatioChip value={profile.expense_income_ratio} />}
        />
        <DataRow label="Saldo actual" value={formatCurrency(profile.current_balance_usd, 'USD')} />
        <DataRow label="Saldo prom 90d" value={formatCurrency(profile.average_balance_90d_usd, 'USD')} />
        <DataRow
          label="Ratio saldo"
          value={<RatioChip value={profile.balance_income_ratio} inverted />}
        />
      </DataCard>

      <DataCard title="Recargas wallet MXN">
        <DataRow label="Mensual (USD)" value={formatCurrency(profile.monthly_wallet_loads_usd, 'USD')} />
        <DataRow
          label="Total 90d / cant"
          value={`${formatCurrency(profile.total_wallet_loads_90d_usd, 'USD')} · ${profile.wallet_load_count_90d}`}
        />
        <DataRow label="Promedio" value={formatCurrency(profile.avg_wallet_load_usd, 'USD')} />
        <DataRow label="Frecuencia" value={profile.wallet_load_frequency} />
        <DataRow label="Última hace" value={`${profile.days_since_last_wallet_load} días`} />
      </DataCard>
    </div>
  );
}

function DataCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col rounded-md border border-border bg-card px-3.5 py-2.5">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <div className="flex flex-1 flex-col justify-center">
        {children}
      </div>
    </div>
  );
}

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-center gap-2 py-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums text-foreground">{value}</span>
    </div>
  );
}

function RatioChip({ value, inverted }: { value: number; inverted?: boolean }) {
  const pct = (value * 100).toFixed(1);
  const isGood = inverted ? value > 0.6 : value < 0.6;
  const isBad = inverted ? value < 0.3 : value > 0.85;
  const variant: 'success' | 'warning' | 'danger' = isBad ? 'danger' : isGood ? 'success' : 'warning';
  return <Badge variant={variant}>{pct}%</Badge>;
}
